import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  AlertOctagon, AlertTriangle, Info, CheckCircle2, TrendingUp, TrendingDown, Minus, ArrowUpRight,
  Plus, Sparkles, LayoutDashboard, Handshake,
} from "lucide-react";
import { useDemoMode } from "@/lib/demoMode";
import SourceCalcBtn from "@/components/SourceCalcBtn";
import DataPartialBanner from "@/components/DataPartialBanner";
import FrictionScoreTranslated from "@/components/FrictionScoreTranslated";
import ProactiveCockpitAssistant from "@/components/ProactiveCockpitAssistant";

const SIGNAL_STYLE = {
  danger: { icon: AlertOctagon, color: "var(--risk-high)", bg: "rgba(226,75,74,0.10)" },
  warning: { icon: AlertTriangle, color: "var(--risk-medium)", bg: "rgba(239,159,39,0.10)" },
  info: { icon: Info, color: "var(--risk-info)", bg: "rgba(55,138,221,0.10)" },
  success: { icon: CheckCircle2, color: "var(--risk-low)", bg: "rgba(29,158,117,0.10)" },
};

// Map a signal's payload onto a deep-link inside the cockpit.
function signalTarget(s, linkBase) {
  // explicit hint from backend wins
  if (s.target_route) return s.target_route;
  if (s.risk_id) return `${linkBase}/risques/${s.risk_id}`;
  // heuristic on textual content
  const blob = `${s.title || ""} ${s.detail || ""}`.toLowerCase();
  if (/contr[oô]le/.test(blob)) return `${linkBase}/controles`;
  if (/incident/.test(blob)) return `${linkBase}/incidents`;
  if (/alert/.test(blob)) return `${linkBase}/alertes`;
  if (/fournisseur|supplier/.test(blob)) return `${linkBase}/fournisseurs`;
  if (/plan d'action|action plan|plan/.test(blob)) return `${linkBase}/plans`;
  if (/rh|hr|personnel/.test(blob)) return `${linkBase}/rh`;
  if (/audit/.test(blob)) return `${linkBase}/audits`;
  if (/financement|asset|actif/.test(blob)) return `${linkBase}/financement`;
  if (/risque|risk/.test(blob)) return `${linkBase}/risques`;
  return `${linkBase}/intelligence`;
}

function MetricCard({ label, value, delta, suffix = "", testid, to, sub, kpiKey }) {
  const isPos = delta >= 0;
  const body = (
    <>
      {kpiKey && <SourceCalcBtn kpiKey={kpiKey} />}
      <div className="text-xs text-zinc-400">{label}</div>
      <div className="flex items-end justify-between mt-3">
        <div className="noam-display text-3xl font-bold">
          {value}{suffix}
        </div>
        <div className={`text-xs flex items-center gap-1 ${isPos ? "text-[#1D9E75]" : "text-[#E24B4A]"}`}>
          {delta === 0 ? <Minus className="w-3 h-3" /> : isPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {delta > 0 ? `+${delta}` : delta}
        </div>
      </div>
      {sub && <div className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">{sub} <ArrowUpRight className="w-2.5 h-2.5" /></div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="noam-card p-5 block relative hover:bg-white/[0.03] transition-colors" data-testid={testid}>
        {body}
      </Link>
    );
  }
  return <div className="noam-card p-5 relative" data-testid={testid}>{body}</div>;
}

function tendanceIcon(t) {
  if (t === "haussiere") return <TrendingUp className="w-3.5 h-3.5 text-[#E24B4A]" />;
  if (t === "baissiere") return <TrendingDown className="w-3.5 h-3.5 text-[#1D9E75]" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
}

function riskColor(score) {
  if (score >= 12) return "var(--risk-high)";
  if (score >= 6) return "var(--risk-medium)";
  return "var(--risk-low)";
}

function RiskMatrix({ risks, linkBase }) {
  // 5x5: x=probabilité (1..5), y=impact (5..1) for visual top=high
  const cellRisks = (p, i) =>
    risks.filter((r) => r.probabilite_nette === p && r.impact_net === i);
  return (
    <div className="noam-card p-5" data-testid="risk-matrix">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="noam-overline">Vue nette</div>
          <div className="noam-display text-base font-semibold">Matrice des risques 5×5</div>
        </div>
        <div className="text-xs text-zinc-500">Impact × Probabilité — cliquez une cellule</div>
      </div>
      <div className="grid grid-cols-[24px_repeat(5,1fr)] gap-1.5">
        <div></div>
        {[1, 2, 3, 4, 5].map((p) => (
          <div key={`p-${p}`} className="text-[10px] text-center text-zinc-500">{p}</div>
        ))}
        {[5, 4, 3, 2, 1].map((i) => (
          <Fragment key={`row-${i}`}>
            <div className="text-[10px] text-zinc-500 flex items-center">{i}</div>
            {[1, 2, 3, 4, 5].map((p) => {
              const score = i * p;
              const list = cellRisks(p, i);
              const hasItems = list.length > 0;
              const to = `${linkBase}/risques?impact=${i}&proba=${p}`;
              return (
                <Link
                  key={`cell-${i}-${p}`}
                  to={to}
                  className={`aspect-square rounded-md border border-white/5 flex items-center justify-center relative transition-all ${hasItems ? "hover:scale-105 hover:border-white/20 cursor-pointer" : "opacity-50"}`}
                  style={{ background: `${riskColor(score)}1A` }}
                  data-testid={`matrix-cell-${p}-${i}`}
                  title={hasItems ? `${list.length} risque(s) · Impact ${i} × Proba ${p}` : `Aucun risque`}
                >
                  {hasItems && (
                    <span
                      className="text-[11px] font-semibold rounded-full w-6 h-6 flex items-center justify-center"
                      style={{ background: riskColor(score), color: "#fff" }}
                    >
                      {list.length}
                    </span>
                  )}
                </Link>
              );
            })}
          </Fragment>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4 text-[10px] text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--risk-low)" }} /> Faible</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--risk-medium)" }} /> Élevé</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--risk-high)" }} /> Critique</span>
      </div>
    </div>
  );
}

function dealScoreColor(s) {
  if (s >= 70) return "#1D9E75";
  if (s >= 45) return "#EF9F27";
  return "#E24B4A";
}

function DealIntelligencePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/deals/intelligence").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || (!data.top?.length && !data.attention?.length)) return null;
  const TrendGlyph = ({ t }) => (
    t === "up" ? <TrendingUp className="w-3.5 h-3.5 text-[#1D9E75]" />
      : t === "down" ? <TrendingDown className="w-3.5 h-3.5 text-[#E24B4A]" />
        : <Minus className="w-3.5 h-3.5 text-zinc-600" />
  );
  return (
    <div className="noam-card p-5" data-testid="deal-intelligence-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="noam-overline">Deal Intelligence</div>
          <div className="noam-display text-base font-semibold flex items-center gap-2">
            <Handshake className="w-4 h-4 text-[#EFD127]" /> Où regarder aujourd'hui
          </div>
        </div>
        <Link to="/app/deals" className="text-xs text-[#378ADD] flex items-center gap-1" data-testid="deal-intel-see-all">
          Registre des deals <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#1D9E75] mb-2">Top opportunités</div>
          {data.top?.length ? (
            <div className="space-y-1.5">
              {data.top.slice(0, 3).map((d) => (
                <Link key={d.id} to="/app/deals" className="flex items-center gap-3 p-2 rounded-lg border border-white/5 hover:border-white/20 transition-colors"
                  data-testid={`deal-intel-top-${d.id}`}>
                  <span className="font-mono text-sm font-bold w-8 text-right" style={{ color: dealScoreColor(d.global) }}>{d.global}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-zinc-200 truncate">{d.title}</div>
                    <div className="text-[10px] text-zinc-500">{d.asset_label}</div>
                  </div>
                  <TrendGlyph t={d.trend} />
                </Link>
              ))}
            </div>
          ) : <div className="text-xs text-zinc-600">Aucun deal scoré pour l'instant.</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#EF9F27] mb-2">Attention requise</div>
          {data.attention?.length ? (
            <div className="space-y-1.5">
              {data.attention.slice(0, 3).map((d) => (
                <Link key={d.id} to="/app/deals" className="flex items-start gap-3 p-2 rounded-lg border border-white/5 hover:border-white/20 transition-colors"
                  data-testid={`deal-intel-attention-${d.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27] mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-zinc-200 truncate">{d.title}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{d.issue}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : <div className="text-xs text-zinc-600">Rien à signaler.</div>}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const isDemo = useDemoMode();
  const risksEndpoint = isDemo ? "/risks" : "/workspace/risks";
  const metricsEndpoint = isDemo ? "/dashboard/metrics" : "/workspace/dashboard/metrics";
  const signalsEndpoint = isDemo ? "/ai/signals" : "/workspace/ai/signals";
  const linkBase = isDemo ? "/demo" : "/app";
  const [metrics, setMetrics] = useState(null);
  const [risks, setRisks] = useState([]);
  const [signals, setSignals] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(metricsEndpoint).then((r) => r.data).catch(() => null),
      api.get(risksEndpoint).then((r) => r.data).catch(() => []),
      api.get(signalsEndpoint).then((r) => r.data).catch(() => []),
    ]).then(([m, r, s]) => {
      setMetrics(m);
      setRisks(r);
      setSignals(s);
    });
  }, [risksEndpoint, metricsEndpoint, signalsEndpoint]);

  const isEmpty = !isDemo && risks.length === 0;
  const priority = [...risks]
    .sort((a, b) => b.impact_net * b.probabilite_nette - a.impact_net * a.probabilite_nette)
    .slice(0, 6);

  if (isEmpty) {
    return (
      <div className="space-y-5" data-testid="dashboard-empty">
        <div>
          <div className="noam-overline">Tableau de bord</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">Vue synthétique de l'environnement</h1>
        </div>
        {/* V23.15.32 — Doctrine 3Q · "Et alors ?" visible même sans risques créés */}
        {!isDemo && (
          <FrictionScoreTranslated size="md" showCta={true} currency="CHF" />
        )}
        {/* V23.15.34 — Assistant proactif de configuration cockpit */}
        {!isDemo && <ProactiveCockpitAssistant />}
        {!isDemo && <DealIntelligencePanel />}
        <div className="noam-card p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-[#378ADD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">Votre tableau de bord se construit avec vos données</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            La matrice 5×5, les signaux IA et les indicateurs apparaîtront dès que vous aurez créé vos premiers risques.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <Link to={`${linkBase}/risques`} className="btn-primary text-sm" data-testid="dash-empty-risques">
              <Plus className="w-4 h-4" /> Créer un risque
            </Link>
            <Link to="/app/configurer" className="btn-ghost text-sm" data-testid="dash-empty-assistant">
              <Sparkles className="w-4 h-4" /> Démarrer avec NOAM
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dashboard-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="noam-overline">Tableau de bord</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">Vue synthétique de l'environnement</h1>
        </div>
        <span className="text-xs text-zinc-500" data-testid="dashboard-updated-at">
          Mis à jour {new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>

      {!isDemo && <DataPartialBanner />}

      {/* V23.15.32 — Doctrine 3Q · "Et alors ?" — Score friction traduit en CHF/an évitables */}
      {!isDemo && (
        <FrictionScoreTranslated size="md" showCta={true} currency="CHF" />
      )}
      {/* V23.15.34 — Assistant proactif de configuration cockpit */}
      {!isDemo && <ProactiveCockpitAssistant />}

      {!isDemo && <DealIntelligencePanel />}

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard testid="metric-risques-critiques" label="Risques critiques" value={metrics.risques_critiques.value}
            delta={metrics.risques_critiques.delta} to={`${linkBase}/risques?level=critical`} sub="Ouvrir la liste filtrée" kpiKey="dash_risques_critiques" />
          <MetricCard testid="metric-controles-retard" label="Contrôles en retard" value={metrics.controles_retard.value}
            delta={metrics.controles_retard.delta} to={`${linkBase}/controles?status=overdue`} sub="Ouvrir les contrôles" kpiKey="dash_controles" />
          <MetricCard testid="metric-conformite" label="Taux de conformité" value={metrics.taux_conformite.value} suffix="%"
            delta={metrics.taux_conformite.delta} to={`${linkBase}/audits`} sub="Voir les audits" kpiKey="dash_conformite" />
          <MetricCard testid="metric-plans" label="Plans d'action actifs" value={metrics.plans_actifs.value}
            delta={metrics.plans_actifs.delta} to={`${linkBase}/plans`} sub="Voir les plans" kpiKey="dash_plans" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="noam-card p-5" data-testid="ai-signals-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="noam-overline">Intelligence IA</div>
              <div className="noam-display text-base font-semibold">Signaux détectés</div>
            </div>
            <Link to={`${linkBase}/intelligence`} className="text-xs text-[#378ADD] flex items-center gap-1">
              Tout voir <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {signals.map((s) => {
              const st = SIGNAL_STYLE[s.type] || SIGNAL_STYLE.info;
              const Icon = st.icon;
              const to = signalTarget(s, linkBase);
              return (
                <Link
                  key={s.id}
                  to={to}
                  className="flex gap-3 p-3 rounded-lg border border-white/5 hover:border-white/20 transition-colors"
                  style={{ background: st.bg }}
                  data-testid={`signal-${s.id}`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: st.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      {s.title}
                      <ArrowUpRight className="w-3 h-3 text-zinc-500" />
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">{s.detail}</div>
                  </div>
                </Link>
              );
            })}
            {signals.length === 0 && (
              <div className="text-xs text-zinc-500 italic">Aucun signal IA pour l'instant.</div>
            )}
          </div>
        </div>

        <RiskMatrix risks={risks} linkBase={linkBase} />
      </div>

      <div className="noam-card p-5" data-testid="priority-risks">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="noam-overline">Priorités</div>
            <div className="noam-display text-base font-semibold">Risques prioritaires</div>
          </div>
          <Link to={`${linkBase}/risques`} className="text-xs text-[#378ADD] flex items-center gap-1">
            Voir tous les risques <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b border-white/5">
            <tr>
              <th className="text-left py-2 font-normal">Code</th>
              <th className="text-left font-normal">Titre</th>
              <th className="text-left font-normal hidden md:table-cell">Catégorie</th>
              <th className="text-center font-normal">Net</th>
              <th className="text-center font-normal">Tend.</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {priority.map((r) => {
              const score = r.impact_net * r.probabilite_nette;
              return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`risk-row-${r.id}`}>
                  <td className="py-3 font-mono text-xs text-zinc-400">{r.code}</td>
                  <td className="text-white">{r.title}</td>
                  <td className="text-zinc-400 hidden md:table-cell">{r.categorie}</td>
                  <td className="text-center">
                    <span
                      className="inline-flex items-center justify-center w-8 h-6 rounded text-xs font-semibold"
                      style={{ background: `${riskColor(score)}26`, color: riskColor(score) }}
                    >
                      {score}
                    </span>
                  </td>
                  <td className="text-center">{tendanceIcon(r.tendance)}</td>
                  <td className="text-right">
                    <Link to={`${linkBase}/risques/${r.id}`} className="text-xs text-[#378ADD]">
                      Détail →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
